"use client"

import * as React from "react"
import { createContext, useContext, useEffect, useState } from "react"

// Define available themes
export type Theme = "light" | "dark" | "system" | "novel" | "claude" | "caffeine" | "arc-rose" | "claude-orange" | "midnight" | "forest" | "dracula"

// Create context for theme management
type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  useEffect(() => {
    const root = window.document.documentElement

    // Remove all existing theme classes
    root.classList.remove("light", "dark", "novel", "claude", "caffeine", "arc-rose", "claude-orange", "midnight", "forest", "dracula")

    // Define which themes should inherit dark mode styles
    const darkThemes = ["dark", "midnight", "forest", "dracula"]

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      root.classList.add(systemTheme)
      return
    }

    // Add the specific theme class
    root.classList.add(theme)

    // specific case for light theme (it's the default, but we might want to be explicit if needed, usually just removing dark is enough)
    // checking if we need to add 'dark' class for tailwind variants
    if (darkThemes.includes(theme)) {
      root.classList.add("dark")
    }
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

// Hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}